import { Component, Inject, Optional, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {
  MatDialogRef,
  MAT_DIALOG_DATA,
  MatDialogContent,
  MatDialogActions,
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatChipsModule, MatChipInputEvent } from '@angular/material/chips';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { TranslateModule } from '@ngx-translate/core';
import { TablerIconsModule } from 'angular-tabler-icons';

import { QrcodeEmail } from '@interfaces/qrcode_email';
import { emailFormatValidator } from '@core/shared/form-validators';
import { TechnicianApiService } from '@services/api/user/technician/technician-api.service';

@Component({
  selector: 'app-email',
  standalone: true,
  templateUrl: './email.component.html',
  styleUrls: ['./email.component.scss'],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogContent,
    MatDialogActions,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatButtonModule,
    MatDividerModule,
    MatChipsModule,
    TranslateModule,
    TablerIconsModule,
  ],
})
export class EmailComponent {
  private technician_api_service = inject(TechnicianApiService);

  separatorKeysCodes: number[] = [ENTER, COMMA];

  emailQrcodeForm: FormGroup;

  constructor(
    public dialogRef: MatDialogRef<EmailComponent>,
    @Optional() @Inject(MAT_DIALOG_DATA) public data: QrcodeEmail | null,
    private fb: FormBuilder
  ) {
    this.emailQrcodeForm = this.fb.group({
      ccEmails: this.fb.control<string[]>([], { nonNullable: true }),
      newEmailCtrl: this.fb.control('', [Validators.email, emailFormatValidator]),
    });

    if (data?.ccEmails) {
      this.emailQrcodeForm.get('ccEmails')?.setValue(data.ccEmails);
    }
  }

  get newEmailCtrl() {
    return this.emailQrcodeForm.get('newEmailCtrl') as FormControl;
  }

  add(event: MatChipInputEvent): void {
    const value = (event.value || '').trim();
    const emailsControl = this.emailQrcodeForm.get('ccEmails') as FormControl<string[]>;
    const currentEmails = emailsControl.value || [];

    if (value && this.newEmailCtrl.valid && !currentEmails.includes(value)) {
      emailsControl.setValue([...currentEmails, value]);
      this.newEmailCtrl.setValue('');
    } else {
      this.newEmailCtrl.markAsTouched();
      this.newEmailCtrl.markAsDirty();
    }

    event.chipInput?.clear();
    this.newEmailCtrl.reset();
  }

  remove(email: string): void {
    const emailsControl = this.emailQrcodeForm.get('ccEmails') as FormControl<string[]>;
    emailsControl.setValue(emailsControl.value.filter(e => e !== email));
  }

  onSubmit(): void {
    if (this.emailQrcodeForm.valid && this.data?.technician_email && this.data?.qr_code) {
      const technicianEmail = this.data.technician_email;
      const qrCodeData = this.data.qr_code;
      const ccEmails = this.emailQrcodeForm.get('ccEmails')?.value || [];

      this.technician_api_service.send_qrcode_email([technicianEmail], ccEmails, qrCodeData).subscribe({
        next: (response) => {
          this.dialogRef.close(response);
        },
        error: (error) => {
          console.error('Errore durante la chiamata API:', error);
        }
      });
    } else {
      this.newEmailCtrl.markAsTouched();
      this.newEmailCtrl.markAsDirty();
    }
  }

  closeModal(): void {
    this.dialogRef.close('close');
  }
}
